import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import type { JobDTO, TargetDTO } from '@devrobot/shared';
import { ArrowRightLeft, Ellipsis, History, Info, Play, RotateCw, Square, Trash2, TriangleAlert, Upload, XCircle } from 'lucide-react';
import { api, newIdempotencyKey, seg } from '../../api/client';
import { qk, useApiMutation, useReleases } from '../../api/queries';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { ErrorAlert } from '../../components/ErrorAlert';
import { MenuPopover, MenuSeparator } from '../../components/MenuPopover';
import { RelativeTime } from '../../components/RelativeTime';
import { useToast } from '../../components/Toaster';
import { Haptic } from '../../kit/haptics';
import { haptic } from '../../kit/native';

const LIFECYCLE_LABEL = { start: 'Start', stop: 'Stop', restart: 'Restart' } as const;

type Dialog = 'stop' | 'restart' | 'rollback' | 'remove' | 'delete-data' | null;

function useTargetMutation<T>(target: TargetDTO, path: string) {
  return useApiMutation((body: T & { idem?: string }) => {
    const { idem, ...rest } = body;
    return api.post<{ job: JobDTO }>(`/api/v1/targets/${seg(target.id)}/${path}`, rest, idem ? { headers: { 'Idempotency-Key': idem } } : undefined);
  }, { invalidate: [qk.target(target.id), qk.targets(target.project.key), ['jobs']] });
}

function serviceNames(t: TargetDTO): string[] {
  const s = new Set<string>(t.state.services.map((x) => x.service));
  if (s.size === 0) {
    t.routes.forEach((r) => s.add(r.service));
    t.ports.forEach((p) => s.add(p.service));
    t.databases.forEach((d) => s.add(d.service));
  }
  return [...s].sort();
}

/** Pending (saved but not applied) configuration, with the apply action (FR-CFG-04). */
export function PendingConfigBanner({ target: t }: { target: TargetDTO }) {
  const navigate = useNavigate();
  const apply = useTargetMutation<{ action: 'apply-config' }>(t, 'actions');
  if (!t.config.pending || t.removedAt) return null;
  return (
    <div role="status" className="alert alert-warning alert-soft items-start">
      <TriangleAlert className="mt-0.5 size-5 shrink-0" aria-hidden="true" />
      <div className="min-w-0 flex-1 space-y-1">
        <p className="font-semibold">Configuration changes are saved but not applied</p>
        <p className="text-sm">The running containers still use the previous configuration. Applying recreates the affected services with the saved values.</p>
        {t.config.pendingReasons.length > 0 ? (
          <ul className="list-disc pl-5 text-sm">
            {t.config.pendingReasons.map((r, i) => (
              <li key={i} className="break-anywhere">
                {r}
              </li>
            ))}
          </ul>
        ) : null}
        <ErrorAlert error={apply.error} title="Configuration was not applied" />
      </div>
      {t.can.operate ? (
        <Haptic disabled={apply.isPending || Boolean(t.activeJob)}>
          <button
            type="button"
            className="btn btn-warning btn-sm"
            disabled={apply.isPending || Boolean(t.activeJob)}
            onClick={() => apply.mutate({ action: 'apply-config', idem: newIdempotencyKey() }, { onSuccess: (r) => navigate(`/jobs/${r.job.id}`), onError: () => haptic('error') })}
          >
            {apply.isPending ? <span className="loading loading-spinner loading-sm" aria-hidden="true" /> : <Upload className="size-4" aria-hidden="true" />}
            Apply configuration
          </button>
        </Haptic>
      ) : null}
    </div>
  );
}

export function TargetActions({ target: t }: { target: TargetDTO }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [dialog, setDialog] = useState<Dialog>(null);
  const lifecycle = useTargetMutation<{ action: 'start' | 'stop' | 'restart' }>(t, 'actions');
  const busy = Boolean(t.activeJob);
  const removed = Boolean(t.removedAt);
  const notDeployed = removed || t.state.process === 'not-deployed';
  const services = serviceNames(t);
  const stops = services.filter((s) => !t.keepRunningOnStop.includes(s));

  const runLifecycle = (action: 'start' | 'stop' | 'restart') =>
    lifecycle.mutate(
      { action },
      {
        onSuccess: (r) => {
          setDialog(null);
          toast.success(`${LIFECYCLE_LABEL[action]} requested`, {
            description: `${t.project.key}/${t.environment} on ${t.vps.name}. Follow the job for progress.`,
            action: { label: 'View job', onClick: () => navigate(`/jobs/${r.job.id}`) },
          });
        },
        onError: () => haptic('error'),
      },
    );

  const canMigrate = t.can.backup && !notDeployed;
  const canRollback = t.can.deploy && !removed && Boolean(t.currentRelease);
  const canRemove = t.can.operate && !removed;
  const anyAction = t.can.operate || t.can.deploy || t.can.deleteData || t.can.backup;
  if (!anyAction) {
    return <p className="text-sm text-base-content/60">Your GitLab role can view this target but not operate it{t.environmentProtected ? ' (protected environments need Maintainer)' : ''}.</p>;
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2" role="toolbar" aria-label="Target actions">
        {t.can.operate && !notDeployed ? (
          <>
            <Haptic disabled={busy || lifecycle.isPending}>
              <button type="button" className="btn btn-sm" disabled={busy || lifecycle.isPending} onClick={() => runLifecycle('start')}>
                {lifecycle.isPending && !dialog ? <span className="loading loading-spinner loading-sm" aria-hidden="true" /> : <Play className="size-4" aria-hidden="true" />} Start
              </button>
            </Haptic>
            <button type="button" className="btn btn-sm" disabled={busy} onClick={() => setDialog('stop')}>
              <Square className="size-4" aria-hidden="true" /> Stop
            </button>
            <button type="button" className="btn btn-sm" disabled={busy} onClick={() => setDialog('restart')}>
              <RotateCw className="size-4" aria-hidden="true" /> Restart
            </button>
          </>
        ) : null}
        {t.can.deleteData && notDeployed ? (
          <button type="button" className="btn btn-sm btn-error" disabled={busy} onClick={() => setDialog('delete-data')}>
            <Trash2 className="size-4" aria-hidden="true" /> Delete data
          </button>
        ) : null}
        {canMigrate || canRollback || canRemove ? (
          <MenuPopover
            label={`More actions for ${t.project.key}/${t.environment}`}
            disabled={busy}
            align="start"
            panelClassName="w-60"
            trigger={
              <>
                <Ellipsis className="size-4" aria-hidden="true" /> More
              </>
            }
          >
            {(close) => (
              <ul className="menu w-full p-0">
                {canMigrate ? (
                  <li>
                    <Link to={`/migrations/new?target=${encodeURIComponent(t.id)}`} onClick={close}>
                      <ArrowRightLeft className="size-4" aria-hidden="true" /> Migrate…
                    </Link>
                  </li>
                ) : null}
                {canRollback ? (
                  <li>
                    <button type="button" onClick={() => (close(), setDialog('rollback'))}>
                      <History className="size-4" aria-hidden="true" /> Roll back
                    </button>
                  </li>
                ) : null}
                {canRemove && (canMigrate || canRollback) ? <MenuSeparator /> : null}
                {canRemove ? (
                  <li>
                    <button type="button" className="dr-ink-error" onClick={() => (close(), setDialog('remove'))}>
                      <XCircle className="size-4" aria-hidden="true" /> Remove application
                    </button>
                  </li>
                ) : null}
              </ul>
            )}
          </MenuPopover>
        ) : null}
        {removed && t.can.deploy ? (
          <Link to={`/projects/${t.project.key}?tab=deploy&env=${encodeURIComponent(t.environment)}&vps=${t.vps.id}`} className="btn btn-sm btn-primary btn-soft">
            Deploy again
          </Link>
        ) : null}
      </div>
      {busy ? <p className="animate-fade text-xs text-base-content/60">Actions are paused while a job runs on this target.</p> : null}
      {!dialog ? <ErrorAlert error={lifecycle.error} title="The action was not started" /> : null}

      {dialog === 'stop' ? (
        <ConfirmDialog title={`Stop ${t.project.key}/${t.environment}?`} confirmLabel="Stop" tone="warning" busy={lifecycle.isPending} error={lifecycle.error} onCancel={() => (setDialog(null), lifecycle.reset())} onConfirm={() => runLifecycle('stop')}>
          <div className="space-y-2 text-sm">
            <p>
              <span className="font-medium">Stops:</span> {stops.length ? stops.map((s) => <code key={s} className="mr-1 font-mono">{s}</code>) : 'all services'}
            </p>
            <p>
              <span className="font-medium">Keeps running:</span>{' '}
              {t.keepRunningOnStop.length ? t.keepRunningOnStop.map((s) => <code key={s} className="mr-1 font-mono">{s}</code>) : 'nothing (the manifest does not keep any service running on stop)'}
            </p>
            <p className="text-base-content/65">Endpoints stop responding. Containers, volumes and configuration are kept; Start brings the application back.</p>
          </div>
        </ConfirmDialog>
      ) : null}
      {dialog === 'restart' ? (
        <ConfirmDialog title={`Restart ${t.project.key}/${t.environment}?`} confirmLabel="Restart" busy={lifecycle.isPending} error={lifecycle.error} onCancel={() => (setDialog(null), lifecycle.reset())} onConfirm={() => runLifecycle('restart')}>
          <p className="text-sm">
            Restarts the containers of {services.length ? services.join(', ') : 'all services'}. Expect a short interruption.
          </p>
          <div role="note" className={`alert ${t.config.pending ? 'alert-warning' : 'alert-info'} alert-soft text-sm`}>
            {t.config.pending ? <TriangleAlert className="size-4 shrink-0" aria-hidden="true" /> : <Info className="size-4 shrink-0" aria-hidden="true" />}
            <span>
              A restart does not apply configuration changes. {t.config.pending ? 'Changes are pending: use Apply configuration to deliver them.' : 'Use Apply configuration after changing values.'}
            </span>
          </div>
        </ConfirmDialog>
      ) : null}
      {dialog === 'rollback' ? <RollbackDialog target={t} onClose={() => setDialog(null)} onStarted={(id) => navigate(`/jobs/${id}`)} /> : null}
      {dialog === 'remove' ? <RemoveDialog target={t} onClose={() => setDialog(null)} onStarted={(id) => navigate(`/jobs/${id}`)} /> : null}
      {dialog === 'delete-data' ? <DeleteDataDialog target={t} onClose={() => setDialog(null)} onStarted={(id) => navigate(`/jobs/${id}`)} /> : null}
    </div>
  );
}

function RollbackDialog({ target: t, onClose, onStarted }: { target: TargetDTO; onClose: () => void; onStarted: (jobId: string) => void }) {
  const releases = useReleases(t.project.key);
  const candidates = (releases.data ?? []).filter((r) => r.id !== t.currentRelease?.id);
  const [releaseId, setReleaseId] = useState('');
  const [configMode, setConfigMode] = useState<'current' | 'release'>('current');
  const [idem] = useState(newIdempotencyKey);
  const rollback = useTargetMutation<{ releaseId: string; configMode: 'current' | 'release' }>(t, 'rollback');
  const selected = candidates.find((r) => r.id === releaseId);
  const migrationWarning = Boolean(t.currentRelease?.migrations || selected?.migrations);

  return (
    <ConfirmDialog
      title="Roll back to an earlier release"
      confirmLabel="Roll back"
      tone="warning"
      size="lg"
      busy={rollback.isPending}
      error={rollback.error}
      confirmDisabled={!releaseId}
      onCancel={onClose}
      onConfirm={() => rollback.mutate({ releaseId, configMode, idem }, { onSuccess: (r) => onStarted(r.job.id) })}
    >
      <p className="text-sm">
        Currently running <span className="font-mono">{t.currentRelease ? `${t.currentRelease.branch}@${t.currentRelease.shortSha}` : 'nothing'}</span>. The earlier release’s images are redeployed; no rebuild is needed while they are retained in the registry.
      </p>
      <fieldset className="fieldset">
        <legend className="fieldset-legend">Release</legend>
        {releases.isPending ? <div className="skeleton h-10 w-full" /> : null}
        <ErrorAlert error={releases.error} />
        {releases.data && candidates.length === 0 ? <p className="text-sm text-base-content/60">There is no other release of this project to roll back to.</p> : null}
        {candidates.length > 0 ? (
          <select className="select w-full font-mono" value={releaseId} onChange={(e) => setReleaseId(e.target.value)}>
            <option value="">Choose a release…</option>
            {candidates.map((r) => (
              <option key={r.id} value={r.id}>
                {r.branch}@{r.shortSha} {r.commitTitle ? `— ${r.commitTitle}` : ''} {r.migrations ? '(migrations)' : ''}
              </option>
            ))}
          </select>
        ) : null}
        {selected ? (
          <p className="label whitespace-normal text-base-content/60">
            Deployed <RelativeTime value={selected.createdAt} />
            {selected.pipelineId ? ` · pipeline #${selected.pipelineId}` : ''}
          </p>
        ) : null}
      </fieldset>
      <fieldset className="fieldset" role="radiogroup" aria-label="Configuration to use">
        <legend className="fieldset-legend">Configuration</legend>
        <label className="flex cursor-pointer items-start gap-3">
          <input type="radio" className="radio radio-sm mt-0.5" checked={configMode === 'current'} onChange={() => setConfigMode('current')} />
          <span className="text-sm">
            <span className="font-medium">Current saved configuration</span> — recommended, especially after rotating secrets.
          </span>
        </label>
        <label className="flex cursor-pointer items-start gap-3">
          <input type="radio" className="radio radio-sm mt-0.5" checked={configMode === 'release'} onChange={() => setConfigMode('release')} />
          <span className="text-sm">
            <span className="font-medium">Configuration recorded with that release</span> — the job reports any secret version that is no longer available.
          </span>
        </label>
      </fieldset>
      {migrationWarning ? (
        <div role="alert" className="alert alert-warning alert-soft animate-rise text-sm">
          <TriangleAlert className="size-4 shrink-0" aria-hidden="true" />
          <span>Database migrations were involved. A rollback redeploys application code only: it does not restore the database. The older release must work with the current schema; restore a backup separately if it does not.</span>
        </div>
      ) : null}
    </ConfirmDialog>
  );
}

function RemoveDialog({ target: t, onClose, onStarted }: { target: TargetDTO; onClose: () => void; onStarted: (jobId: string) => void }) {
  const [removeImages, setRemoveImages] = useState(true);
  const [removeFiles, setRemoveFiles] = useState(true);
  const remove = useTargetMutation<{ removeImages: boolean; removeFiles: boolean }>(t, 'remove');
  const volumes = t.volumes.map((v) => v.name);
  return (
    <ConfirmDialog
      title={`Remove ${t.project.key} from ${t.environment} on ${t.vps.name}?`}
      confirmLabel="Remove application"
      tone="error"
      size="lg"
      busy={remove.isPending}
      error={remove.error}
      onCancel={onClose}
      onConfirm={() => remove.mutate({ removeImages, removeFiles }, { onSuccess: (r) => onStarted(r.job.id) })}
    >
      <div className="space-y-3 text-sm">
        <p>
          <span className="font-medium">Removed:</span> the containers and DevRobot-owned networks of <code className="font-mono">{t.stack}</code>, and its proxy routes. Its endpoints stop responding.
        </p>
        <p>
          <span className="font-medium">Kept:</span> persistent volumes {volumes.length ? volumes.map((v) => <code key={v} className="mr-1 font-mono">{v}</code>) : '(none declared)'}, including database data, and the configuration stored in DevRobot. Deploying again reuses them.
        </p>
        <label className="flex cursor-pointer items-start gap-3">
          <input type="checkbox" className="checkbox checkbox-sm mt-0.5" checked={removeImages} onChange={(e) => setRemoveImages(e.target.checked)} />
          <span>Also remove this application’s images from the server (frees disk; a new deployment pulls them again).</span>
        </label>
        <label className="flex cursor-pointer items-start gap-3">
          <input type="checkbox" className="checkbox checkbox-sm mt-0.5" checked={removeFiles} onChange={(e) => setRemoveFiles(e.target.checked)} />
          <span>Also remove delivered private configuration files from the server (they stay stored in DevRobot).</span>
        </label>
      </div>
    </ConfirmDialog>
  );
}

function DeleteDataDialog({ target: t, onClose, onStarted }: { target: TargetDTO; onClose: () => void; onStarted: (jobId: string) => void }) {
  const [selected, setSelected] = useState<Set<string>>(new Set(t.volumes.filter((v) => v.declared).map((v) => v.name)));
  const del = useTargetMutation<{ volumes: string[]; confirm: string }>(t, 'delete-data');
  const phrase = `delete ${t.stack}`;
  const dbs = t.databases.filter((d) => selected.has(d.volume));
  return (
    <ConfirmDialog
      title="Permanently delete persistent data"
      confirmLabel="Delete data permanently"
      tone="error"
      size="lg"
      typedConfirmation={phrase}
      busy={del.isPending}
      error={del.error}
      confirmDisabled={selected.size === 0}
      onCancel={onClose}
      onConfirm={() => del.mutate({ volumes: [...selected], confirm: phrase }, { onSuccess: (r) => onStarted(r.job.id) })}
    >
      <div className="space-y-3 text-sm">
        <div role="alert" className="alert alert-error alert-soft">
          <Trash2 className="size-4 shrink-0" aria-hidden="true" />
          <span>
            The selected Docker volumes of <code className="font-mono">{t.stack}</code> on {t.vps.name} are deleted. Everything stored in them is lost and cannot be recovered by DevRobot
            {dbs.length ? `, including the ${dbs.map((d) => `${d.type} database "${d.service}"`).join(' and ')}` : ''}.
          </span>
        </div>
        {t.volumes.length === 0 ? <p>No volumes are known for this target.</p> : null}
        <fieldset className="space-y-1.5">
          <legend className="mb-1 font-medium">Volumes</legend>
          {t.volumes.map((v) => (
            <label key={v.name} className="flex cursor-pointer items-center gap-3">
              <input
                type="checkbox"
                className="checkbox checkbox-sm checkbox-error"
                checked={selected.has(v.name)}
                onChange={(e) => {
                  const n = new Set(selected);
                  if (e.target.checked) n.add(v.name);
                  else n.delete(v.name);
                  setSelected(n);
                }}
              />
              <code className="font-mono">{v.name}</code>
              <span className="text-xs text-base-content/60">{v.exists === true ? 'exists' : v.exists === false ? 'not found on the server' : 'existence unknown'}</span>
            </label>
          ))}
        </fieldset>
        <p className="text-base-content/65">Backups stored by DevRobot are not deleted. If you may need this data, confirm a verified backup exists first (Backups tab).</p>
      </div>
    </ConfirmDialog>
  );
}
